<template>
  <div class="cnc-badge-label">
    <div :class="`badge-label ${sizeBadge[size]} ${getStyle()} ${outline ? 'badge-outline' : ''} ${className}`">
      <span v-if="leftIcon && leftIcon.name" class="icon-left">
        <CncIcon
          :name="leftIcon.name"
          :color="leftIcon.color ? leftIcon.color : getColorSvgIcon()"
          :width="getIconWidth()"
          :isSolid="leftIcon.isSolid"
          :isTwoTone="leftIcon.isTwoTone"
          :isGradient="leftIcon.isGradient"
        />
      </span>
      <span v-if="currency" data-qa="txt-currency">{{ currency }}</span>
      <span>{{ trimmedLabelText }}</span>
      <slot/>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    label: { type: [String, Number], required: true },
    type: { type: String, default: 'primary' },
    size: { type: String, default: 'large' },
    styleBadge: { type: String, default: 'bold' },
    leftIcon: { type: Object },
    isNumber: { type: Boolean },
    isAbbreviated: { type: Boolean },
    isPrice: { type: Boolean },
    currency: { type: String },
    outline: { type: Boolean },
    limitCharacter: { type: Number, default: 24 },
    className: { type: String, default: '' },
    isTruncate: { type: Boolean }
  },
  data () {
    return {
      colorStyle: {
        'primary': this.$store.state.color['primary-500'],
        'secondary': this.$store.state.color['secondary-500'],
        'neutral': this.$store.state.color['coolGrey-600'],
        'positive': this.$store.state.color['semanticGreen-500'],
        'critical': this.$store.state.color['semanticRed-500'],
        'in-progress': this.$store.state.color['semanticYellow-500'],
        'warning': this.$store.state.color['semanticOrange-500'],
        'informing': this.$store.state.color['semanticBlue-500'],
        'concerning': this.$store.state.color['semanticViolet-500']
      },
      sizeBadge: {
        'large': 'badge-large',
        'medium': 'badge-medium',
        'small': 'badge-small',
        'extra-small': 'badge-extra-small'
      }
    }
  },
  computed: {
    trimmedLabelText () {
      if (this.isNumber && !isNaN(parseFloat(this.label)) && isFinite(this.label)) {
        const number = parseInt(this.label)
        if (this.isAbbreviated) return number > 999 ? this.abbreviateNumber(number) : number
        else if (this.isPrice) return number.toLocaleString()
        else return number > 999 ? '999+' : number
      } else {
        if (this.isTruncate) {
          if (this.label.length > this.limitCharacter) return this.label.slice(0, this.limitCharacter - 1) + '...'
          return this.label
        }
        return this.label
      }
    }
  },
  methods: {
    abbreviateNumber (num) {
      const abbreviations = ['', 'K', 'M', 'B', 'T']
      const numString = num.toString()
      const abbreviationIndex = Math.floor((numString.length - 1) / 3)
      const abbreviatedNumber = (num / Math.pow(1000, abbreviationIndex)).toFixed(2)
      const roundedNumber = parseFloat(abbreviatedNumber)
      return roundedNumber.toString() + abbreviations[abbreviationIndex]
    },
    getStyle () {
      switch (this.styleBadge) {
        case 'bold': return `badge-bold-${this.type}`
        case 'subtle': return `badge-subtle-${this.type}`
        case 'minimal': return `badge-minimal-${this.type}`
        case 'text': return `badge-text-${this.type}`
        case 'bold-on-color': return `badge-bold-color-${this.type}`
        case 'outlined-on-color': return `badge-outlined-color-${this.type}`
        case 'text-on-color': return `badge-text-color-${this.type}`
        default : return ''
      }
    },
    getColorSvgIcon () {
      return this.styleBadge === 'bold' ? '#fff' : this.colorStyle[this.type]
    },
    getIconWidth () {
      return this.size === 'large' ? 20 : this.size === 'medium' ? 16 : this.size === 'small' ? 12 : this.size === 'extra-small' ? 12 : 16
    }
  }
}
</script>

<style lang="scss">
.cnc-badge-label {
  display: inline-block;
  width: fit-content;

  .badge-label {
    display: inline-flex;
    background-color: $color-primary-500;
    color: $color-white;
    border-radius: 999px;
    border: 1px solid;
    gap: 4px;

    .icon-left {
      display: inline-flex;
      align-items: center;
    }

    &.badge-large {
      height: $space-xl;
      font-size: $text-base-px;
      line-height: 24px;
      padding: 4px 12px;

      svg {
        width: $text-lg-px !important;
        height: $text-lg-px !important;
      }

      i {
        font-size: $text-lg-px;
      }
    }

    &.badge-medium {
      height: $space-lg;
      font-size: $text-sm-px;
      line-height: 21px;
      padding: 2px 8px;

      svg {
        width: $text-base-px !important;
        height: $text-base-px !important;
      }

      i {
        font-size: $text-base-px;
      }
    }

    &.badge-small {
      height: $space-md;
      font-size: $text-xs-px;
      line-height: 18px;
      padding: 1px 8px;

      svg {
        width: $text-xs-px !important;
        height: $text-xs-px !important;
      }

      i {
        font-size: $text-xs-px;
      }
    }

    &.badge-extra-small {
      height: $space-sm;
      font-size: $text-2xs-px;
      padding: 0px 4px;
      line-height: 15px;

      svg {
        width: $text-xs-px !important;
        height: $text-xs-px !important;
      }

      i {
        font-size: $text-xs-px;
      }
    }

    //bold

    &.badge-bold-primary {
      color: $color-white;
      background-color: $color-primary-500;
      border-color: $color-primary-500;
    }

    &.badge-bold-secondary {
      color: $color-white;
      background-color: $color-secondary-500;
      border-color: $color-secondary-500;
    }

    &.badge-bold-neutral {
      color: $color-white;
      background-color: $color-cool-grey-500;
      border-color: $color-cool-grey-500;
    }

    &.badge-bold-positive {
      color: $color-white;
      background-color: $color-semanticGreen-500;
      border-color: $color-semanticGreen-500;
    }

    &.badge-bold-critical {
      color: $color-white;
      background-color: $color-semanticRed-500;
      border-color: $color-semanticRed-500;
    }

    &.badge-bold-in-progress {
      color: $color-white;
      background-color: $color-semanticYellow-500;
      border-color: $color-semanticYellow-500;
    }

    &.badge-bold-warning {
      color: $color-white;
      background-color: $color-semanticOrange-500;
      border-color: $color-semanticOrange-500;
    }

    &.badge-bold-informing {
      color: $color-white;
      background-color: $color-semanticBlue-500;
      border-color: $color-semanticBlue-500;
    }

    &.badge-bold-concerning {
      color: $color-white;
      background-color: $color-semanticViolet-500;
      border-color: $color-semanticViolet-500;
    }

    // subtle
    &.badge-subtle-primary {
      color: $color-primary-500;
      background-color: $color-primary-50;
      border-color: $color-primary-50;
    }

    &.badge-subtle-secondary {
      color: $color-secondary-500;
      background-color: $color-secondary-50;
      border-color: $color-secondary-50;
    }

    &.badge-subtle-neutral {
      color: $color-cool-grey-600;
      background-color: $color-cool-grey-100;
      border-color: $color-cool-grey-100;
    }

    &.badge-subtle-positive {
      color: $color-semanticGreen-500;
      background-color: $color-semanticGreen-100;
      border-color: $color-semanticGreen-100;
    }

    &.badge-subtle-critical {
      color: $color-semanticRed-500;
      background-color: $color-semanticRed-50;
      border-color: $color-semanticRed-50;
    }

    &.badge-subtle-in-progress {
      color: $color-semanticYellow-500;
      background-color: $color-semanticYellow-50;
      border-color: $color-semanticYellow-50;
    }

    &.badge-subtle-warning {
      color: $color-semanticOrange-500;
      background-color: $color-semanticOrange-50;
      border-color: $color-semanticOrange-50;
    }

    &.badge-subtle-informing {
      color: $color-semanticBlue-500;
      background-color: $color-semanticBlue-50;
      border-color: $color-semanticBlue-50;
    }

    &.badge-subtle-concerning {
      color: $color-semanticViolet-500;
      background-color: $color-semanticViolet-50;
      border-color: $color-semanticViolet-50;
    }

    //minimal
    &.badge-minimal-primary {
      color: $color-primary-500;
      background-color: $color-white;
      border-color: $color-cool-grey-100;
    }

    &.badge-minimal-secondary {
      color: $color-secondary-500;
      background-color: $color-white;
      border-color: $color-cool-grey-100;
    }

    &.badge-minimal-neutral {
      color: $color-cool-grey-500;
      background-color: $color-white;
      border-color: $color-cool-grey-100;
    }

    &.badge-minimal-positive {
      color: $color-semanticGreen-500;
      background-color: $color-white;
      border-color: $color-cool-grey-100;
    }

    &.badge-minimal-critical {
      color: $color-semanticRed-500;
      background-color: $color-white;
      border-color: $color-cool-grey-100;
    }

    &.badge-minimal-in-progress {
      color: $color-semanticYellow-500;
      background-color: $color-white;
      border-color: $color-cool-grey-100;
    }

    &.badge-minimal-warning {
      color: $color-semanticOrange-500;
      background-color: $color-white;
      border-color: $color-cool-grey-100;
    }

    &.badge-minimal-informing {
      color: $color-semanticBlue-500;
      background-color: $color-white;
      border-color: $color-cool-grey-100;
    }

    &.badge-minimal-concerning {
      color: $color-semanticViolet-500;
      background-color: $color-white;
      border-color: $color-cool-grey-100;
    }

    //text
    &.badge-text-primary {
      color: $color-primary-500;
      background-color: transparent;
      border-color: transparent;
    }

    &.badge-text-secondary {
      color: $color-secondary-500;
      background-color: transparent;
      border-color: transparent;
    }

    &.badge-text-neutral {
      color: $color-cool-grey-500;
      background-color: transparent;
      border-color: transparent;
    }

    &.badge-text-positive {
      color: $color-semanticGreen-500;
      background-color: transparent;
      border-color: transparent;
    }

    &.badge-text-critical {
      color: $color-semanticRed-500;
      background-color: transparent;
      border-color: transparent;
    }

    &.badge-text-in-progress {
      color: $color-semanticYellow-500;
      background-color: transparent;
      border-color: transparent;
    }

    &.badge-text-warning {
      color: $color-semanticOrange-500;
      background-color: transparent;
      border-color: transparent;
    }

    &.badge-text-informing {
      color: $color-semanticBlue-500;
      background-color: transparent;
      border-color: transparent;
    }

    &.badge-text-concerning {
      color: $color-semanticViolet-500;
      background-color: transparent;
      border-color: transparent;
    }

    //Bold on color
    &.badge-bold-color-primary {
      color: $color-primary-500;
      background-color: $color-white;
      border-color: $color-white;
    }

    &.badge-bold-color-secondary {
      color: $color-secondary-500;
      background-color: $color-white;
      border-color: $color-white;
    }

    &.badge-bold-color-neutral {
      color: $color-cool-grey-500;
      background-color: $color-white;
      border-color: $color-white;
    }

    &.badge-bold-color-positive {
      color: $color-semanticGreen-500;
      background-color: $color-white;
      border-color: $color-white;
    }

    &.badge-bold-color-critical {
      color: $color-semanticRed-500;
      background-color: $color-white;
      border-color: $color-white;
    }

    &.badge-bold-color-in-progress {
      color: $color-semanticYellow-500;
      background-color: $color-white;
      border-color: $color-white;
    }

    &.badge-bold-color-warning {
      color: $color-semanticOrange-500;
      background-color: $color-white;
      border-color: $color-white;
    }

    &.badge-bold-color-informing {
      color: $color-semanticBlue-500;
      background-color: $color-white;
      border-color: $color-white;
    }

    &.badge-bold-color-concerning {
      color: $color-semanticViolet-500;
      background-color: $color-white;
      border-color: $color-white;
    }

    //Outlined on color
    &.badge-outlined-color-primary, &.badge-outlined-color-secondary, &.badge-outlined-color-neutral, &.badge-outlined-color-positive, &.badge-outlined-color-critical, &.badge-outlined-color-in-progress, &.badge-outlined-color-warning, &.badge-outlined-color-informing, &.badge-outlined-color-concerning {
      color: $color-white;
      background-color: transparent;
      border-color: $color-white;
    }

    //Text on color
    &.badge-text-color-primary, &.badge-text-color-secondary, &.badge-text-color-neutral, &.badge-text-color-positive, &.badge-text-color-critical, &.badge-text-color-in-progress, &.badge-text-color-warning, &.badge-text-color-informing, &.badge-text-color-concerning {
      color: $color-white;
      background-color: transparent;
      border-color: transparent;
    }

    &.badge-outline {
      outline: 2px solid $color-white;
    }
  }
}
</style>
